import React from 'react'
import { RealEstateCategory, SegmentationType, TransactionType } from '../../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../../@types/flow-types'
import { TENANT_APARTMENT_RENT_FLOW_PATH } from '../../../../../../services/routes/routes'
import { tenantApartmentRentSchema } from './tenantApartmentRentSchema'
import {
  SCREEN_ADDRESS,
  SCREEN_BASIC_INFO,
  SCREEN_CONTACT_INFO,
  SCREEN_COSTS,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OTHER_FACILITIES,
  SCREENS,
} from '../../../../../../services/domain/wizardScreens'
import ContactInfo from '../../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Address from '../../../../../../components/pages/insertionPage/screens/address/Address'
import { priceToApiMonthlyRent } from '../../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'
import Multimedia from '../../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import { MultimediaUploaderType } from '../../../../../../@types/multimedia-types'
import ResidentialCostsRent from '../../../../../../components/pages/insertionPage/screens/costs/wrappers/residential/ResidentialCostsRent'
import Description from '../../../../../../components/pages/insertionPage/screens/description/Description'
import OtherFacilities from '../../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import {
  bathroomField,
  floorAndTotalFloor,
  heatingTypeField,
  interiorQuality,
  noBedrooms,
  parkingSpace,
  petsAllowed,
  usableAreaField,
} from '../../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import { getDefaultClasses } from '../../../../../../utils/style-utils'
import BasicInfo from '../../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  livingSpaceField,
  priceField,
  priceMaximizer,
  roomField,
} from '../../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { PriceFieldType } from '../../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { buildingTypeField } from '../../../../../../components/pages/insertionPage/screens/commonFields/buildingType/BuildingType'
import { tenantApartmentRentEstateFeatures, tenantApartmentRentBuildingTypeOptions } from '../common'

const tenantApartmentRentFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        screenTooltipKey='tooltip.screens.basicInfo.apartmentHouseOrSTARent'
        properties={[
          livingSpaceField(),
          roomField(),
          priceField(PriceFieldType.COLD_RENT, 'one-half palm-padding-left-m lap-padding-right-m desk-padding-right-m'),
          priceMaximizer(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_COSTS],
    element: (props) => (
      <ResidentialCostsRent {...props} showMoveInDate={true} screenTooltipKey='tooltip.screens.costs.allRent' />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => (
      <OtherFacilities
        {...props}
        screenTooltipKey='tooltip.screens.otherFacilities.allRent'
        properties={[
          tenantApartmentRentEstateFeatures(),
          interiorQuality(),
          petsAllowed(),
          buildingTypeField('apartment', tenantApartmentRentBuildingTypeOptions, getDefaultClasses('full')),
          heatingTypeField(getDefaultClasses('full')),
          bathroomField(getDefaultClasses('left')),
          noBedrooms('right'),
          floorAndTotalFloor('left'),
          usableAreaField(undefined, getDefaultClasses('rightOnDesk')),
          parkingSpace({ showParkingSpaceType: true, hasGarageLabel: true }),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasLocationNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[MultimediaUploaderType.PICTURE, MultimediaUploaderType.VIDEO]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
]

export const tenantApartmentRentFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.tenant',
    estateType: 'wizard.navigation.header.apartment',
    path: TENANT_APARTMENT_RENT_FLOW_PATH,
    name: FlowName.TENANT_APARTMENT_RENT,
    validationSchema: tenantApartmentRentSchema,
    options: {
      hasTextSuggestions: true,
      depositCalculationEnabled: true,
    },
    metaData: {
      category: RealEstateCategory.APARTMENT,
      segmentation: SegmentationType.TENANT,
      transaction: TransactionType.RENT,
    },
    priceToApiMapper: priceToApiMonthlyRent,
  },
  defaultStateValues: {
    hideStreetAndStreetNumber: false,
    heatingCostsOption: true,
  },
  flowScreens: tenantApartmentRentFlowScreens,
}
